import { recipeSrcSet } from "../lib/media";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Clock, Users, Flame, ArrowLeft, Minus, Plus, ChefHat } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { Loader } from "../components/shared";
import BackToHome from "../components/BackToHome";
import { WhatsAppShareButton } from "../components/WhatsAppShare";
import { CookMode } from "../components/CookMode";

export default function RecipeDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [portions, setPortions] = useState(null);
  const [cooking, setCooking] = useState(false);

  useEffect(() => {
    api.get(`/content/recipes/${id}?lang=${lang}`).then(({ data }) => { setR(data); setPortions(data.servings); }).catch(() => navigate("/app/recipes"));
  }, [id, lang, navigate]);

  if (!r) return <Loader />;
  const base = r.servings || 1;
  const factor = +(portions / base).toFixed(2);

  return (
    <div data-testid="recipe-detail">
      {cooking && <CookMode recipe={r} onClose={() => setCooking(false)} />}
      <BackToHome crumbs={[{ label: t("nav.recipes"), to: "/app/recipes" }]} current={r.name} />
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-2 text-sm text-xmas-gold hover:underline">
        <ArrowLeft className="h-4 w-4" /> {t("cta.back")}
      </button>

      <div className="overflow-hidden rounded-3xl border border-xmas-gold/15">
        <div className="relative h-64 sm:h-80">
          <img src={r.image} srcSet={recipeSrcSet(r.image)} sizes="(max-width: 640px) 100vw, 1024px" alt={r.name} decoding="async" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-xmas-navy via-xmas-navy/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6">
            <h1 className="font-display text-3xl font-bold text-xmas-snow sm:text-4xl">{r.name}</h1>
          </div>
        </div>

        <div className="bg-xmas-navycard p-6 sm:p-8">
          <p className="text-base text-xmas-slate">{r.description}</p>
          <div className="mt-5 flex flex-wrap gap-5 text-sm text-xmas-snow">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-xmas-gold" />{r.time_minutes} {t("recipes.min")}</span>
            <span className="flex items-center gap-2"><Flame className="h-4 w-4 text-xmas-gold" />{t(`diff.${r.difficulty}`)}</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-xmas-gold" />{r.servings} {t("recipes.servings")}</span>
          </div>

          {/* Portions scaler */}
          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-xl bg-xmas-navy/40 px-4 py-4" data-testid="recipe-scaler">
            <div>
              <p className="text-sm text-xmas-snow">{t("recipe.for_guests")}</p>
              <p className="text-xs text-xmas-slate">{t("recipe.base", { n: base })}</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setPortions((p) => Math.max(1, p - 1))} data-testid="recipe-portions-minus" className="flex h-9 w-9 items-center justify-center rounded-full border border-xmas-gold/40 text-xmas-gold hover:bg-xmas-gold/10" aria-label="minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-mono-num text-2xl text-xmas-snow" data-testid="recipe-portions-value">{portions}</span>
              <button onClick={() => setPortions((p) => p + 1)} data-testid="recipe-portions-plus" className="flex h-9 w-9 items-center justify-center rounded-full border border-xmas-gold/40 text-xmas-gold hover:bg-xmas-gold/10" aria-label="plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {factor !== 1 && (
              <span className="rounded-full bg-xmas-gold/15 px-3 py-1 text-xs text-xmas-gold" data-testid="recipe-factor">×{factor}</span>
            )}
          </div>
          <p className="mt-2 text-xs italic text-xmas-slate" data-testid="recipe-scaled-hint">{t("recipe.scaled_hint", { n: portions, f: factor })}</p>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <h2 className="mb-4 font-display text-xl text-xmas-gold">{t("recipes.ingredients")}</h2>
              <ul className="space-y-2">
                {r.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-xmas-snow">
                    <span className="h-1.5 w-1.5 rounded-full bg-xmas-gold" /> {ing}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button onClick={() => setCooking(true)} className="btn-gold inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wider" data-testid="recipe-cook-mode-button">
                  <ChefHat className="h-4 w-4" /> {t("cook.open")}
                </button>
                <WhatsAppShareButton recipe={r} lang={lang} className="py-3" />
              </div>
            </div>
            <div>
              <h2 className="mb-4 font-display text-xl text-xmas-gold">{t("recipes.steps")}</h2>
              <ol className="space-y-4">
                {r.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-xmas-snow">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xs text-xmas-gold">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
